import { firebaseApp } from "@/app/plugins/firebase";
import type { FeatureAccessMap } from "@/shared/types/featureAccess";
import { RemoteConfig, fetchAndActivate, getRemoteConfig, getValue } from "firebase/remote-config";

const FEATURE_ACCESS_KEYS = ["featureAccessMap", "feature_access_map"];

let remoteConfigInstance: RemoteConfig | null = null;

const initializeRemoteConfig = (): RemoteConfig | null => {
  if (typeof window === "undefined") {
    return null;
  }

  if (!remoteConfigInstance) {
    remoteConfigInstance = getRemoteConfig(firebaseApp);
    remoteConfigInstance.settings = {
      minimumFetchIntervalMillis: import.meta.env.DEV ? 0 : 5 * 60 * 1000,
      fetchTimeoutMillis: 10_000,
    };
    remoteConfigInstance.defaultConfig = FEATURE_ACCESS_KEYS.reduce(
      (defaults, key) => {
        defaults[key] = "{}";
        return defaults;
      },
      {} as Record<string, string>,
    );
  }

  return remoteConfigInstance;
};

const activateRemoteConfig = async (config: RemoteConfig): Promise<void> => {
  try {
    await fetchAndActivate(config);
  } catch {}
};

export const getRemoteFeatureAccessMap = async (): Promise<FeatureAccessMap> => {
  const remoteConfig = initializeRemoteConfig();

  if (!remoteConfig) {
    return {};
  }

  await activateRemoteConfig(remoteConfig);

  let fallbackMap: FeatureAccessMap | null = null;

  for (const key of FEATURE_ACCESS_KEYS) {
    const rawValue = getValue(remoteConfig, key).asString();

    if (!rawValue) {
      continue;
    }

    try {
      const parsed = JSON.parse(rawValue);
      const normalized = parsed && typeof parsed === "object" ? parsed : {};
      if (Object.keys(normalized).length > 0) {
        return normalized;
      }
      fallbackMap = normalized;
    } catch {
      return {};
    }
  }

  if (fallbackMap) {
    return fallbackMap;
  }

  return {};
};
